import { Box, Grid, LinearProgress } from '@material-ui/core';
import { getLoanById } from '@nemuru-eng/js-api-client';
import { useParams } from 'react-router-dom';

import { LoanHeaderDetails } from '@/components/modules/ProductsDetailsOverview/components/LoanHeaderDetails';
import { OrderHeaderDetails } from '@/components/modules/ProductsDetailsOverview/components/OrderHeaderDetails';
import { useLiveLoan } from '@/components/modules/ProductsDetailsOverview/hooks/useLiveLoan';
import { LoanApplications } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/LoanApplications';
import { LoanApplicationMode } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/types/LoanApplicationMode';
import { Participants } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/Participants/Participants';
import { LoanConditionsSummary } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/LoanConditionsSummary';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { Progress } from '@/components/shared/components/Progress/Progress';
import { ApplicationTypeMap } from '@/config/maps/ApplicationType.map';
import { ApplicationType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';
import { useScreen } from '@/hooks/useScreen';

interface ProductDetailsOverviewProps {
  showClientData: boolean;
  enableApplicationProcessing: boolean;
  mode: LoanApplicationMode;
}

export const ProductDetailsOverview = ({
  showClientData,
  enableApplicationProcessing,
  mode,
}: ProductDetailsOverviewProps) => {
  const { loanId } = useParams();

  if (!loanId) return null;

  const { data: loan } = useCache(getLoanById, { loanId });
  useLiveLoan(loanId);
  const { isMobile } = useScreen();

  const applicationType = ApplicationTypeMap.fromAssetType(loan?.assetReference[0].type);

  if (!loan || !applicationType) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  const headerDetails = () => {
    switch (applicationType) {
      case ApplicationType.ORDER:
      case ApplicationType.PAY_BY_LINK:
        return <OrderHeaderDetails loanId={loanId} showClientData={showClientData} />;
      case ApplicationType.CREDIT_LINE:
      case ApplicationType.AUTO:
      case ApplicationType.CONSUMER_LOAN:
      case ApplicationType.CARD:
        return <LoanHeaderDetails loan={loan} showClientData={showClientData} />;
      default:
        return <LoanHeaderDetails loan={loan} showClientData={showClientData} />;
    }
  };

  return (
    <>
      <Box mb={6}>{headerDetails()}</Box>
      <Grid container spacing={6}>
        <Grid item xs={12} lg={6}>
          <LoanConditionsSummary loan={loan} applicationType={applicationType} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Progress loan={loan} applicationType={applicationType} />
        </Grid>
      </Grid>
      {!isMobile && (
        <>
          <CustomDivider orientation={'horizontal'} margin={5} />
        </>
      )}
      <Grid container spacing={6}>
        <Grid item xs={12} lg={6}>
          <Participants agentId={loan.agentId} merchantId={loan.merchantId} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <LoanApplications enableApplicationProcessing={enableApplicationProcessing} mode={mode} />
        </Grid>
      </Grid>
    </>
  );
};
